import {
  Virtualizer,
  elementScroll,
  observeElementOffset,
  observeElementRect,
  type VirtualItem,
  type VirtualizerOptions,
} from "@tanstack/react-virtual";

export type TableVirtualOptions = Pick<
  VirtualizerOptions<HTMLDivElement, HTMLDivElement>,
  "count" | "getScrollElement" | "getItemKey" | "estimateSize" | "overscan" | "measureElement"
>;

interface VirtualRowsSnapshot {
  readonly rows: readonly VirtualItem[];
  readonly totalSize: number;
}

function sameRow(left: VirtualItem, right: VirtualItem) {
  return (
    left.key === right.key &&
    left.index === right.index &&
    left.start === right.start &&
    left.end === right.end &&
    left.size === right.size &&
    left.lane === right.lane
  );
}

function sameSnapshot(left: VirtualRowsSnapshot, right: VirtualRowsSnapshot) {
  return (
    left.totalSize === right.totalSize &&
    left.rows.length === right.rows.length &&
    left.rows.every((row, index) => sameRow(row, right.rows[index]))
  );
}

/** Expose immutable snapshots to React, never the core's mutable render getters. */
export class VirtualRowsStore {
  private readonly listeners = new Set<() => void>();
  private readonly instance: Virtualizer<HTMLDivElement, HTMLDivElement>;
  private snapshot: VirtualRowsSnapshot;

  constructor(options: TableVirtualOptions) {
    this.instance = new Virtualizer<HTMLDivElement, HTMLDivElement>(this.resolvedOptions(options));
    this.snapshot = this.readRows();
  }

  private resolvedOptions(options: TableVirtualOptions) {
    return {
      observeElementRect,
      observeElementOffset,
      scrollToFn: elementScroll,
      ...options,
      onChange: () => this.publish(),
    };
  }

  private readRows(): VirtualRowsSnapshot {
    return {
      rows: this.instance.getVirtualItems().map((row) => ({ ...row })),
      totalSize: this.instance.getTotalSize(),
    };
  }

  private publish() {
    const next = this.readRows();
    if (sameSnapshot(this.snapshot, next)) return;
    this.snapshot = next;
    this.listeners.forEach((listener) => listener());
  }

  getSnapshot = () => this.snapshot;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  mount = () => this.instance._didMount();

  update(options: TableVirtualOptions) {
    this.instance.setOptions(this.resolvedOptions(options));
    this.instance._willUpdate();
    this.publish();
  }

  measureElement = (element: HTMLDivElement | null) => this.instance.measureElement(element);
}
